// floatShellGeometry — フローティングパネル (S2) の DOM 非依存な純ロジック
// (docs/_wip/floating-panels-2026-08-01.md §9「純ロジックは DOM 非依存の純関数に切り出して
// node テストで固定する」)。
//
// 背景: FloatShell (`FloatShell.tsx`) はドックから切り離されたパネルを
// `position: fixed` で自由配置する。ここに集約するのは:
//   - **grip からの pointer 座標 → パネル着地点の変換** (`floatPositionFromPointer`)。
//     §11 改訂 (consult 2026-08-01「掴んだ位置を保持する」) により、掴んだ点と対象要素
//     左上の相対距離 (grab-offset) を pointerdown 時点で実測し (`usePanelDragGrip`)、
//     ドラッグ中ずっと保持して使う (= 固定オフセット近似は廃止。窓のどこを掴んでも
//     その相対位置が維持され、右ドックへ寄せる操作でカーソルが窓幅ぶん余計に必要だった
//     実害を解消する)。
//   - **viewport clamp** (`clampFloatPosition`)。画面外救済 (§9「ロード時と resize 時に
//     viewport clamp」) と、画面端インセット (`oekaki/CLAUDE.md`「スマホの画面端スワイプ」+
//     §9「OS ジェスチャゾーン対策として clamp 下限を物理画面端から ~32px インセット」) の
//     両方を **同じ 1 つの margin** (`FLOAT_EDGE_INSET_PX`) で満たす。フロートパネル内に
//     全幅の水平ドラッグ UI (太さスライダー等) を将来足すときも、パネル自体がこのインセット
//     より内側にしか存在しないため、パネル内 UI は自動的に画面端から離れる (= 追加設計不要)。
//   - **z-order** (`bringToFront`)。クリック/ドラッグ開始で最前面へ (§9「z-order: canvas より
//     上・モーダル/ConfirmDialog より下。クリック/ドラッグ開始で最前面へ」)。
//
// Classic 既存の floating 部品 (`floatingChatPopup.ts` の `clampPopupPosition` 等) と同種の
// clamp 数式だが、**S3 (Classic 側) は本件のスコープ外**なので独立した実装として置く
// (Classic のファイルには一切触れない、S3 で共有化を検討)。

/** フロートパネルの画面位置 (px, viewport 座標系)。`WorkspaceState.floating` の値と同じ形。 */
export interface FloatPoint {
  readonly x: number;
  readonly y: number;
}

/** パネルの実測サイズ (px)。clamp の上限計算に使う。 */
export interface FloatSize {
  readonly width: number;
  readonly height: number;
}

/**
 * 2 つのサイズが「実質同じ」か (= 再計算/setState の抑止用、`floatingSlot.slotRectEquals`
 * と同じ流儀)。`FloatShell` の `ResizeObserver` はサブピクセルの揺れを高頻度で報告しうるので、
 * 呼び出し側 (`onMeasure` の受け手) がこれで比較して有意差の無い更新を捨てる。
 */
export const sizeEquals = (a: FloatSize, b: FloatSize, epsilon = 0.5): boolean =>
  Math.abs(a.width - b.width) < epsilon && Math.abs(a.height - b.height) < epsilon;

/** viewport サイズ (px)。 */
export interface ViewportSize {
  readonly width: number;
  readonly height: number;
}

/**
 * `floatPositionFromPointer` の防御的フォールバック値 (px)。**通常経路では使われない**。
 *
 * 確定方針 (§11「掴んだ位置を保持する」、consult 2026-08-01) は実測 grab-offset
 * (`usePanelDragGrip` が pointerdown 時点で「掴んだ点 − 対象要素左上」を計測し、ドラッグ中
 * ずっと保持する値) を渡すこと。旧実装 (S2 初版) はこの固定値だけで「グリップ帯の見た目位置に
 * 指を残したまま追従する」近似をしていたが、掴み所をシェル全体へ広げた修正 (バグ修正
 * 2026-08-01「フロートウィンドウをドラッグしても動かないことがある」) で前提が崩れた:
 * 窓のどこを掴んでも左上がカーソルへ飛んで付け直され、特に右ドックへ寄せる操作で「窓は
 * 既に右端に達しているのに、カーソルは窓幅ぶん (244〜260px) さらに右へ運ばないとエッジ判定
 * に入らない」実害が出ていた (§11 参照)。
 *
 * 残す理由: `ModernLayout` の grab-offset state は drag start
 * (`handlePanelDragStart`/`FloatShell` の `onPointerDragStart`) で必ず設定されるため通常は
 * 存在しないが、万一の欠落 (将来のリファクタ漏れ等の防御) でも `floatPositionFromPointer`
 * が未定義座標を返して窓が原点付近へ吹っ飛ぶような致命的破綻にしないための最終フォールバック。
 */
export const FLOAT_GRAB_OFFSET: FloatPoint = { x: 12, y: 8 };

/**
 * ドラッグ中の pointer 座標 (client 系) から、フロートパネルの左上座標を算出する純関数。
 * `offset` (= 実測 grab-offset、呼び出し側が pointerdown 時点で計測・保持した値。無ければ
 * `FLOAT_GRAB_OFFSET` を渡す) を引く。clamp はしない (= 呼び出し側が `clampFloatPosition`
 * を通す、責務分離)。
 *
 * §11 改訂により offset は**呼び出し側が明示的に渡す必須引数**にした (= 固定値へ暗黙に
 * フォールバックしない設計にして、呼び出し側が実測値を渡し忘れる事故を型で防ぐ)。
 */
export const floatPositionFromPointer = (
  clientX: number,
  clientY: number,
  offset: FloatPoint,
): FloatPoint => ({
  x: clientX - offset.x,
  y: clientY - offset.y,
});

/**
 * 実測 grab-offset (= 掴んだ点と対象要素左上の相対距離) を対象サイズの範囲へクランプする
 * (§11「detach 時の注意」)。ドック内パネルとフロート時では幅が変わりうる (`floatWidthFor`
 * が返すフロート幅に対し、ドックは splitter で可変幅) ため、実測 offset をそのまま保持すると
 * 掴み点がフロート本体/ゴーストの外に出ることがある (= 追従描画上、パネル左上がカーソルより
 * 外側に来て見た目が破綻する)。各軸独立に `[0, size]` へ収める。
 */
export const clampGrabOffset = (offset: FloatPoint, size: FloatSize): FloatPoint => ({
  x: Math.min(Math.max(offset.x, 0), size.width),
  y: Math.min(Math.max(offset.y, 0), size.height),
});

/**
 * フロートパネルを画面内 (かつ物理端から `insetPx` 以上内側) に収める clamp (px)。
 *
 * `oekaki/CLAUDE.md`「スマホの画面端スワイプ『戻る』は web で止められない」の教訓により、
 * 既定インセットは **32px** (= OS ジェスチャゾーン ~24dp の外)。`ModernPhoneShell` の
 * `SHEET_EDGE_INSET_CLASS` (実測 33px) と揃えた値。
 *
 * 各軸独立に `min(max(pos, inset), max(inset, viewport - size - inset))` で clamp する
 * (`floatingChatPopup.clampPopupPosition` と同型の数式。パネルが viewport よりも大きい
 * ケースでも `max(inset, ...)` ガードにより左上が必ず画面内 = 掴んで戻せる位置に残る)。
 */
export const FLOAT_EDGE_INSET_PX = 32;

export const clampFloatPosition = (
  pos: FloatPoint,
  size: FloatSize,
  viewport: ViewportSize,
  insetPx: number = FLOAT_EDGE_INSET_PX,
): FloatPoint => ({
  x: Math.min(Math.max(pos.x, insetPx), Math.max(insetPx, viewport.width - size.width - insetPx)),
  y: Math.min(Math.max(pos.y, insetPx), Math.max(insetPx, viewport.height - size.height - insetPx)),
});

/**
 * z-order 配列 (= 背面→前面の id 列) の末尾へ id を送る (= 最前面化)。**既に登録済みで
 * かつ末尾にある**ときだけそのまま同一参照を返す (= 無駄な setState/rerender を避ける
 * 呼び出し側の最適化に使える)。未知の id はそのまま末尾に追加する (= 初回クリック時に
 * 配列へ登録する用途を兼ねる)。
 *
 * バグ修正 (2026-08-01 実機検証): 早期 return の判定を `idx === order.length - 1` **だけ**
 * にしていたため、**`order` が空配列のとき `idx=-1` と `order.length-1=-1` が一致してしまい
 * 「未登録の id」を「既に最前面」と誤判定して no-op になっていた** (= フロートを何枚出しても
 * `floatOrder` が永遠に空のまま → 全パネルが `zIndexFromOrder` の未登録 fallback (base) に
 * 張り付き、クリック/ドラッグで前面化が一切効かない実害が出た)。`idx >= 0` を先に確認し、
 * 「配列に実在し、かつ末尾」のときだけ早期 return する。
 */
export const bringToFront = <T>(order: ReadonlyArray<T>, id: T): ReadonlyArray<T> => {
  const idx = order.indexOf(id);
  if (idx >= 0 && idx === order.length - 1) return order;
  const rest = order.filter((x) => x !== id);
  return [...rest, id];
};

/**
 * z-order 配列上の id の位置から CSS `z-index` を算出する。base (= 既存の floating 部品と
 * 同じ帯、`ColorWheelPalette`/`LayerPopup`/`PenPopup` 等が使う `z-40`。CanvasStage の
 * `z-index:1` より上、モーダル/ConfirmDialog の `z-50`/`z-[60]` より下) に配列内順位を足す。
 *
 * id が配列に無ければ base をそのまま返す (= 「まだ一度もクリック/ドラッグで前面化された
 * ことが無い」パネルの意味。`bringToFront` のバグ修正後はこの状態が正常に起こりうる —
 * detach 直後や、永続化から復元されて今セッションでまだ触っていないフロートパネルがこれに
 * 当たる。複数の未登録パネルは全部 base で並ぶが、DOM 出現順で描画されるので実害はない
 * (= 一度でもクリック/ドラッグすれば `bringToFront` で確実に配列へ登録され、以後は他の
 * 未登録パネルより前面に出る)。
 */
export const FLOAT_Z_BASE = 40;

export const zIndexFromOrder = <T>(order: ReadonlyArray<T>, id: T): number => {
  const idx = order.indexOf(id);
  return idx < 0 ? FLOAT_Z_BASE : FLOAT_Z_BASE + idx;
};

/**
 * detach 中 (= ドック内パネルをドラッグして「離すとフロートになる」領域にいる間) に描く
 * プレビューの z-index (consult 2026-08-01 追加依頼「detach 中のフロートプレビュー」)。
 * ドラッグ中は常に最前面であるべきだが、モーダル/ConfirmDialog (`z-50`/`z-[60]`) より下に
 * 留める必要がある。フロート可能パネルは高々 6 枚 (toolProperties/colorPanel/colorPalette/
 * previewPanel/layerPanel/socialDock) なので `zIndexFromOrder` が返しうる最大値は
 * `FLOAT_Z_BASE + 5 = 45` — その 1 つ上の 46 を固定値で確保しておけば、既存の全フロートより
 * 確実に前面かつモーダルより確実に下、という安全域に収まる。
 */
export const FLOAT_DETACH_PREVIEW_Z = FLOAT_Z_BASE + 6;

/**
 * フロートパネルが画面の左半分・右半分どちらに居るかを判定する (S2 §11 改訂、consult
 * 2026-08-01「⇤ドック → 位置ベース + 方向ラベル」)。「保持する state を持たず、現在位置から
 * 都度導出する」という確定方針の核: パネルの**水平中心** (`position.x + size.width/2`) と
 * **viewport の水平中心** (`viewport.width/2`) を比較するだけの純関数。
 *
 * layout row (`layoutRowRef`) は viewport 幅をほぼ占有する (左右に大きな余白を持たない
 * デスクトップシェル前提) ため、viewport 中心を row 中心の近似として使う。これにより
 * render 中に ref を読まずに済み (= 純粋な計算、`viewportSizeNow()` の呼び出し元が唯一の
 * 副作用アクセス)、SSR/計測前でも安全に呼べる。
 *
 * 境界 (ちょうど中央) は **'right' 側**とする (`< center` だけを 'left' 判定にする、
 * 明示的に固定・テストで担保)。
 */
export const floatPanelSide = (
  position: FloatPoint,
  size: FloatSize,
  viewport: ViewportSize,
): 'left' | 'right' => {
  const panelCenterX = position.x + size.width / 2;
  const viewportCenterX = viewport.width / 2;
  return panelCenterX < viewportCenterX ? 'left' : 'right';
};

/**
 * detach 直後 (= まだ `FloatShell` が一度も実測していない、今セッションでこの panelId を
 * 初めて浮かせる瞬間) だけに使う暫定サイズ。
 *
 * バグ修正 (2026-08-01 実機フィードバック「パレット/レイヤーの背の低いフロートが画面下まで
 * 移動できない」): 当初この定数を**常に**ドラッグ中の描画位置 clamp と drop 確定の着地点
 * clamp の両方で使っていたため (= 実測を一切参照しない)、背の低いパネル (レイヤー/パレット
 * 等、実高さ ~150px) でも clamp の可動域が高さ 400px 前提で計算され、viewport 下端よりずっと
 * 手前で頭打ちになっていた (例: viewport 950px なら `950-400-32=518` が y の上限になり、
 * 実際は `950-150-32=768` まで動けるはずなのに大きなデッドゾーンが残った)。
 *
 * 修正: `FloatShell` が `onMeasure` で実測サイズを都度 (mount 時 + ResizeObserver) 親
 * (`ModernLayout`) へ報告し、親は panelId → 実測サイズを state (`floatSizes`) で保持して
 * **ドラッグ中の描画位置と drop 確定の着地点、両方の clamp サイズにそれを使う**
 * (`floatSizeFor`)。本定数はその state にまだエントリが無い場合 (= 今セッションで一度も
 * 浮いたことがない、正真正銘の初回) だけの近似に用途を限定する。一度でも実測されれば以後は
 * 常に実測値が使われ、この定数は二度と参照されない (= 「初回のみの近似」)。
 */
export const FLOAT_PANEL_FALLBACK_HEIGHT = 400;

/**
 * 現在の viewport サイズ (px)。SSR / 非ブラウザ環境では `fallback` を返す
 * (= このアプリはルーム画面が client-only だが、防御的に guard する)。
 */
export const viewportSizeNow = (
  fallback: ViewportSize = { width: 1280, height: 800 },
): ViewportSize =>
  typeof window === 'undefined'
    ? fallback
    : { width: window.innerWidth, height: window.innerHeight };

/**
 * シェル全体ドラッグの対象**外** (= ここにヒットしたら pointerdown を grip へ委譲しない)。
 * `e.target.closest(NO_SHELL_DRAG_SELECTOR)` で判定する。
 *
 * 元々 `FloatShell.tsx` にのみ private const として置いていたが、Classic の
 * サイドバー一体フロート (docs/_wip/floating-panels-2026-08-01.md §12) が
 * **同じ「シェル全体を掴めるが、ボタン等インタラクティブ要素は除外する」判定**を
 * 必要としたため、判定系を二重に作らないよう共有 geometry へ引き上げた
 * (= `FloatShell.tsx` もここから import する)。
 *
 * - `button/input/select/textarea/a`: 標準のインタラクティブ要素。
 * - `canvas`: 自前 pointer-capture を持つカスタム操作 (ArgbSliderPanel の ARGB
 *   キャンバス、プレビューナビゲータ等) — `setPointerCapture` の二重取り合いを
 *   避けるため丸ごと除外する。
 * - `[role="button"]` / `[role="slider"]` / `[role="separator"]`: ARIA ロールで表す
 *   カスタムウィジェット (色相リング、splitter 等)。
 * - `[role="dialog"]`: パネル内から開くネストしたポップアップの保護。
 * - `[contenteditable="true"]`: 将来のリッチ入力に備えた防御的追加。
 */
export const NO_SHELL_DRAG_SELECTOR =
  'button, input, select, textarea, a, canvas, [role="button"], [role="slider"], [role="separator"], [role="dialog"], [contenteditable="true"]';
